import {useCallback, useEffect, useRef, useState} from "react";
import {productDraft, removeProduct, saveProduct, validateProductDraft} from "./product-catalogue-api";

export default function useProductEditor(language, onSaved, onRemoved) {
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState(null);
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);
    const scope = useRef(null);
    useEffect(() => {
        const current = {active: true, pending: false, controller: new AbortController()};
        scope.current = current;
        return () => {current.active = false; current.controller.abort();};
    }, []);
    const cancel = useCallback(() => {
        if (!scope.current?.pending) {setEditing(null); setDraft(null); setErrors({}); setNotice(null);}
    }, []);
    const edit = row => {setEditing(row); setDraft(productDraft(row)); setErrors({}); setNotice(null);};
    const mutate = async (row, removing = false) => {
        const current = scope.current;
        if (!current?.active || current.pending) return false;
        const invalid = removing ? {} : validateProductDraft(draft);
        setErrors(invalid);
        if (Object.keys(invalid).length) return false;
        current.pending = true; setBusy(true); setNotice(null);
        try {
            const options = {signal: current.controller.signal, timeout: 15000,
                headers: {"Language-Context": (language || "en").toUpperCase()}};
            const saved = removing ? await removeProduct(row, options) : await saveProduct(row, draft, options);
            if (!current.active) return false;
            if (removing) onRemoved(); else {onSaved(saved); setEditing(null); setDraft(null);}
            setNotice({rowId: row.productTenantId, product: row.productName, severity: "success", message: removing ? "ProductsRemoved" : "ProductsSaved"});
            return true;
        } catch (error) {
            if (current.active) setNotice({rowId: row.productTenantId, severity: "error",
                product: row.productName,
                message: error.response?.data?.message || (error.message?.startsWith("Products") ? error.message : "ProductsSaveFailed")});
            return false;
        } finally {current.pending = false; if (current.active) setBusy(false);}
    };
    return {editing, draft, errors, busy, notice, setNotice, edit, cancel, setDraft,
        save: () => mutate(editing), remove: row => mutate(row, true)};
}
